// Транспорт и хуки раздела «Путь сделки» (ручки ядра /chain/*). ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/client.ts: там всё построено на эмуляции Bitrix REST
// (POST /api/bx с method/params), а здесь обычные GET-ручки ядра. Тот же порядок, что
// у api/products.ts и api/p73.ts — форма ответа и разбор ошибок общие для всех модулей ядра.
//
// Записи здесь нет и появиться не должно: путь сделки — это сводка по шести смарт-процессам,
// двум таблицам портала 73 и составу сделки. Каждый из них правится своим бизнес-процессом
// в Bitrix, и «поправить цепочку» — бессмысленная операция: править надо звено, у себя.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import { источник } from './istochnik'
import { БАЗА, ПУТИ, РАЗМЕР_СТРАНИЦЫ } from '../lib/chain'
import type {
  СписокРазрывов, СправочникиПути, ПутьСделки, ФильтрыРазрывов,
} from '../lib/chain'

/** Тот же разбор ответа, что в api/client.ts, api/p73.ts и api/products.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой запрос обслужить нельзя». Пустой список здесь читался бы как
    // «разрывов нет», а это самая дорогая ложь, какую способен сказать этот раздел.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    // 404 на сделку — законный ответ, а не сбой: сделки может не быть в зеркале.
    if (res.status === 404) throw new CoreUnsupported(String(rec.why ?? 'не найдено'))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

/**
 * Ядро иногда не умеет то, что просят (старая версия, не залитый источник). Повторять такой
 * запрос бессмысленно: ответ не изменится, а человек будет ждать. Ровно то же правило
 * в api/products.ts.
 */
const общее = {
  retry: (попытка: number, ошибка: unknown) =>
    !(ошибка instanceof CoreUnsupported) && попытка < 1,
} as const

/**
 * Путь одной сделки. Дороже обычной карточки: ядро смотрит в шесть смарт-процессов, портал 73,
 * состав и журнал переносов (замер на живом ядре — около 0,5 с). Поэтому держим результат
 * свежим минуту: человек листает звенья и возвращается, и перезапрашивать это на каждый
 * клик незачем.
 */
export const useПутьСделки = (id?: string | number) =>
  useQuery({
    // Источник — часть ключа кэша: после переключения own/mirror ответ другой,
    // и показывать старый под новым источником нельзя.
    queryKey: ['chain', 'deal', источник(), String(id ?? '')],
    queryFn: () => запрос<ПутьСделки>(ПУТИ.сделка(id as string | number)),
    enabled: !!id,
    staleTime: 60_000,
    ...общее,
  })

/**
 * Сделки с разрывом. `placeholderData` оставляет предыдущую страницу на экране, пока едет
 * следующая: иначе при листании таблица моргает пустотой, а пустота здесь читается как
 * «разрывов больше нет».
 */
export const useРазрывы = (ф: ФильтрыРазрывов = {}) =>
  useQuery({
    queryKey: ['chain', 'gaps', источник(), ф],
    queryFn: () => запрос<СписокРазрывов>(ПУТИ.разрывы({ limit: РАЗМЕР_СТРАНИЦЫ, ...ф })),
    placeholderData: (prev) => prev,
    ...общее,
  })

/**
 * Справочники раздела: из чего состоит цепочка, с какого дня ведут каждое звено, сколько
 * сейчас разрывов какого вида. Считается по окну и на живом ядре занимает ~0,6 с, поэтому
 * держим 10 минут: эти числа меняются с синком, а не с каждым открытием экрана.
 */
export const useСправочникиПути = (дней?: number) =>
  useQuery({
    queryKey: ['chain', 'refs', источник(), дней ?? 'по умолчанию'],
    queryFn: () => запрос<СправочникиПути>(ПУТИ.справочники(дней)),
    staleTime: 10 * 60_000,
    ...общее,
  })
